/**
 * ProfileContext — global state for the Profile feature.
 *
 * The auth user (src/lib/useAuth.js) only carries the bare identity fields
 * needed by headers/menus (name, handle, tone, stats). Profile needs more —
 * bio, location, favorite ordering, privacy — so this context layers that
 * additive data on top rather than bloating useAuth. Persisted via
 * useLocalStore, same idiom as MyWriting's story persistence.
 */

import React, { createContext, useContext, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useLocalStore } from '@/lib/useLocalStore';
import { useAuth } from '@/lib/useAuth';
import { PROFILE_EXTRAS, SEED_SCRIBBLES } from '../constants/seedProfile';

const ProfileContext = createContext(undefined);

export function ProfileProvider({ children }) {
  const { username: routeUsername } = useParams();
  const { user, getProfile, uploadBanner: uploadBannerApi, updateProfile: updateProfileApi } = useAuth();
  const [extras, setExtras] = useLocalStore('sr-profile-extras', PROFILE_EXTRAS);
  const [scribbles, setScribbles] = useLocalStore('sr-profile-scribbles', SEED_SCRIBBLES);

  useEffect(() => {
    let active = true;

    const username = routeUsername || user?.username || user?.handle;
    if (!username) return undefined;

    getProfile(username)
      .then((payload) => {
        if (!active) return;
        const remoteProfile = payload?.profile || payload?.data?.profile || payload?.data || payload;
        if (!remoteProfile || typeof remoteProfile !== 'object') return;

        const remotePrivacy = remoteProfile.privacy || {};
        const displayName = remoteProfile.displayName || remoteProfile.name || remoteProfile.username || '';

        setExtras((previous) => ({
          ...previous,
          ...remoteProfile,
          name: displayName,
          handle: remoteProfile.username || remoteProfile.handle,
          initial: displayName.charAt(0).toUpperCase(),
          tone: remoteProfile.avatarTone || remoteProfile.tone,
          followers: remoteProfile.followerCount ?? remoteProfile.followers,
          following: remoteProfile.followingCount ?? remoteProfile.following,
          bannerImage: remoteProfile.bannerUrl ?? remoteProfile.bannerImage,
          joinedAt: remoteProfile.createdAt ? new Date(remoteProfile.createdAt).getTime() : remoteProfile.joinedAt,
          privacy: {
            ...previous.privacy,
            ...remotePrivacy,
            ...(remoteProfile.showReadingActivity === undefined ? {} : { showReadingActivity: remoteProfile.showReadingActivity }),
            ...(remoteProfile.showEmail === undefined ? {} : { showEmail: remoteProfile.showEmail }),
            ...(remoteProfile.isPublic === undefined ? {} : { publicProfile: remoteProfile.isPublic }),
          },
        }));
      })
      .catch((error) => {
        console.error('[Profile API] Failed to load profile:', error);
      });

    return () => { active = false; };
  }, [getProfile, routeUsername, setExtras, user]);

  const profile = useMemo(() => (user ? { ...user, ...extras } : null), [user, extras]);

  const updateProfile = useCallback(async (updates) => {
    const nextPrivacy = { ...extras.privacy, ...(updates.privacy ?? {}) };
    await updateProfileApi({
      bio: updates.bio ?? profile?.bio ?? '',
      location: updates.location ?? profile?.location ?? '',
      website: updates.website ?? profile?.website ?? '',
      showReadingActivity: !!nextPrivacy.showReadingActivity,
      showEmail: !!nextPrivacy.showEmail,
      isPublic: !!nextPrivacy.publicProfile,
    });
    setExtras((prev) => ({ ...prev, ...updates }));
  }, [extras.privacy, profile, setExtras, updateProfileApi]);

  const updatePrivacy = useCallback(async (updates) => {
    const nextPrivacy = { ...extras.privacy, ...updates };
    await updateProfileApi({
      bio: profile?.bio ?? '',
      location: profile?.location ?? '',
      website: profile?.website ?? '',
      showReadingActivity: !!nextPrivacy.showReadingActivity,
      showEmail: !!nextPrivacy.showEmail,
      isPublic: !!nextPrivacy.publicProfile,
    });
    setExtras((prev) => ({ ...prev, privacy: { ...prev.privacy, ...updates } }));
  }, [extras.privacy, profile, setExtras, updateProfileApi]);

  const updateBanner = useCallback(async (file) => {
    if (!file) {
      setExtras((prev) => ({ ...prev, bannerImage: null }));
      return null;
    }
    const payload = await uploadBannerApi(file);
    const result = payload?.data?.profile || payload?.data || payload?.profile || payload;
    const bannerImage = result?.bannerUrl || result?.banner_url || result?.url;
    if (!bannerImage) throw new Error('The banner upload did not return an image URL.');
    setExtras((prev) => ({ ...prev, bannerImage }));
    return bannerImage;
  }, [setExtras, uploadBannerApi]);

  const reorderFavorites = useCallback((dragId, targetId) => {
    setExtras((prev) => {
      const order = [...(prev.favoriteOrder ?? [])];
      const from = order.indexOf(dragId);
      const to = order.indexOf(targetId);
      if (from === -1 || to === -1) return prev;
      order.splice(from, 1);
      order.splice(to, 0, dragId);
      return { ...prev, favoriteOrder: order };
    });
  }, [setExtras]);

  const setFavoriteOrder = useCallback((order) => {
    setExtras((prev) => ({ ...prev, favoriteOrder: order }));
  }, [setExtras]);

  const toggleScribbleLike = useCallback((scribbleId) => {
    setScribbles((prev) => prev.map((s) => (
      s.id === scribbleId ? { ...s, liked: !s.liked, likes: s.likes + (s.liked ? -1 : 1) } : s
    )));
  }, [setScribbles]);

  const value = useMemo(() => ({
    profile,
    updateProfile,
    updatePrivacy,
    updateBanner,
    favoriteOrder: extras.favoriteOrder ?? [],
    reorderFavorites,
    setFavoriteOrder,
    scribbles,
    toggleScribbleLike,
  }), [profile, updateProfile, updatePrivacy, updateBanner, extras.favoriteOrder, reorderFavorites, setFavoriteOrder, scribbles, toggleScribbleLike]);

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

export function useProfile() {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useProfile must be used within ProfileProvider');
  return ctx;
}
